import { useState, type PointerEvent, type ReactNode } from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { OcrTextDragItem } from '@/pages/document/components/DocumentDragAndDrop/dragData';
import type {
  DisplayedOcrPolygon,
  DisplayedOcrPolygonRect,
  OcrBoundingPoly,
  OcrBox,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/ocrGeometry';
import type { OcrWordsSelection } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/useOcrWordsSelection';
import { OcrWordsArea } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/StyledComponents';
import { GrabbedOcrWordFrame } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/OcrWordsDragSource/StyledComponents';

type Grab = { item: OcrTextDragItem; wordRect: DisplayedOcrPolygonRect | null };

type Props = {
  pageNumber: number;
  words: ReadonlyArray<OcrBox>;
  displayedWords: ReadonlyArray<DisplayedOcrPolygon>;
  selection: OcrWordsSelection | null;
  children: ReactNode;
};

const OcrWordsDragSource = ({ pageNumber, words, displayedWords, selection, children }: Props) => {
  // Set on the press; the drag starts 4px later, after the render that hands it to dnd-kit.
  const [grab, setGrab] = useState<Grab | null>(null);
  const { setNodeRef, listeners, isDragging } = useDraggable({ id: `ocr-words-${pageNumber}`, data: grab?.item });

  const grabAt = (handle: HTMLElement): Grab | null => {
    const { right, top, height } = handle.getBoundingClientRect();
    const lineStart = { x: right, y: top + height / 2 };
    const { ocrWordIndex } = handle.dataset;
    const toItem = (text: string, { x, y, w, h }: OcrBoundingPoly): OcrTextDragItem => ({
      kind: 'ocrText',
      text,
      ocrBoundingPoly: { x, y, h, w, page_number: pageNumber },
      lineStart,
    });
    if (ocrWordIndex === undefined) {
      return selection && { item: toItem(selection.text, selection.ocrBoundingPoly), wordRect: null };
    }
    const index = Number(ocrWordIndex);
    const { text, left, top: wordTop, width, height: wordHeight } = displayedWords[index];
    return {
      item: toItem(text, words[index].boundingPoly),
      wordRect: { left, top: wordTop, width, height: wordHeight },
    };
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const handle = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-document-drag-handle]') : null;
    setGrab(handle && grabAt(handle));
    listeners?.onPointerDown(event);
  };

  return (
    <OcrWordsArea ref={setNodeRef} onPointerDown={onPointerDown} id="Polygons-container">
      {children}
      {isDragging && grab?.wordRect && <GrabbedOcrWordFrame style={grab.wordRect} />}
    </OcrWordsArea>
  );
};

export default OcrWordsDragSource;
